class Quiz extends HTMLElement {
  constructor() {
    super();

    this.listId = this.dataset.klaviyoListId;
    this.totalQuestions = parseFloat(this.dataset.totalQuestions);
    this.percentage = 0;
    this.score = 'X OF Y';
    this.currentIndex = 0;
    this.timerInterval = null;

    this.eventListeners();
  }

  eventListeners() {
    this.quizControls.forEach((button) => {
      button.addEventListener("click", this.changeQuestion.bind(this));
    });

    this.quizAnswers.forEach((answer) => {
      answer.addEventListener("change", this.answerInteraction.bind(this));
    });

    if (this.form) {
      this.form.addEventListener("submit", this.submitResults.bind(this));
    }
  }

  changeQuestion(button) {
    const goToIndex = parseFloat(button.currentTarget.dataset.index);
    const goToScreen = this.quizScreens[goToIndex];

    this.hideAllQuestions();

    if (this.quizScreens[goToIndex]) {
      this.quizScreens[goToIndex].setAttribute("data-visible", true);
    }

    if (goToScreen.dataset.quizScreen === "timed") {
      this.startTimer(goToScreen);
    }

    this.currentIndex = goToIndex;

    if (this.currentIndex > this.totalQuestions) {
      this.showResults();
    }
    this.scrollToTop();
  }

  scrollToTop() {
    const header = document.querySelector('.header');
    const headerHeight = header ? header.offsetHeight : 0;

    window.scrollTo({ top: this.offsetTop - headerHeight, behavior: "smooth" });
  }

  answerInteraction() {
    const controls = this.quizScreens[this.currentIndex].querySelector(`[data-quiz-control]`);
    if (controls) {
      controls.removeAttribute("disabled");
    }
  }

  disableAnswers(screen) {
    screen.querySelectorAll(`[data-quiz-answer]`).forEach((input) => {
      input.setAttribute("disabled", true);
    });
  }

  hideAllQuestions() {
    this.quizScreens.forEach((screen) => {
      screen.setAttribute("data-visible", false);
    });
  }

  startTimer(screen) {
    if (screen.dataset.timer === "finished") return;

    const timer = screen.querySelector(`[data-timer-duration]`);
    if (!timer) return;

    const timerDuration = parseInt(timer.dataset.timerDuration, 10);
    if (Number.isNaN(timerDuration) || timerDuration < 0) return;

    clearInterval(this.timerInterval);

    let remaining = timerDuration;
    timer.innerHTML = remaining;

    this.timerInterval = setInterval(() => {
      remaining -= 1;
      timer.innerHTML = remaining;

      if (remaining <= 0) {
        clearInterval(this.timerInterval);
        screen.dataset.timer = "finished";
        this.disableAnswers(screen);
        this.answerInteraction();
      }
    }, 1000);
  }

  showResults() {
    const correctAnswers = this.allAnswers;
    const userAnswers = this.selectedAnswers;

    let score = 0;
    for (let i = 0; i < correctAnswers.length; i++) {
      if (userAnswers[i] === correctAnswers[i]) {
        score++;
      }
    }

    this.score = `${score} OF ${correctAnswers.length}`;
    this.percentage = Math.round((score / correctAnswers.length) * 100);

    if (this.resultsElement) {
      this.resultsElement.textContent = this.score;
    }

    if (this.progressElement) {
      this.progressElement.style.setProperty("--percentage", this.percentage);
      this.progressElement.querySelector(".progress-wrapper__amount").textContent = `${this.percentage}%`;
    }
  }

  submitResults(e) {
    e.preventDefault();

    const payload = this.createPayload();
    const submitButton = this.form.querySelector("button[type='submit']");

    if (!payload || !window.klaviyoCompanyId || !this.listId) {
      return;
    }

    fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${window.klaviyoCompanyId}`, {
      method: 'POST',
      headers: {
        accept: 'application/vnd.api+json',
        'content-type': 'application/vnd.api+json',
        revision: '2026-01-15',
      },
      body: JSON.stringify(payload),
    })
      .then((result) => {
        if (result.ok) {
          this.formMessage(window.quizFormStrings.success, 'success');
          submitButton.style.display = 'none';
        } else {
          this.formMessage(window.quizFormStrings.error, 'error');
        }
      })
      .catch((error) => {
        this.formMessage(window.quizFormStrings.error, 'error');
      });
  }

  createPayload() {
    const email = this.form.querySelector("[data-quiz-email]").value.trim();

    if (!email || !this.listId) {
      return null;
    }

    const payload = {
      data: {
        type: 'subscription',
        attributes: {
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email,
                properties: {
                  [`quiz_${this.listId}_score`]: this.score,
                  [`quiz_${this.listId}_percentage`]: this.percentage,
                }
              }
            }
          }
        },
        relationships: {
          list: {
            data: {
              type: 'list',
              id: this.listId
            }
          }
        },
      },
    };

    return payload;
  }

  formMessage(message, state) {
    this.message.textContent = message;
    this.message.classList.remove('form__success', 'form__error');
    this.message.classList.add(`form__${state}`);
  }

  /**
 * Returns all quiz screens
 * @returns {NodeList} A list of quiz screen elements.
 */
  get quizScreens() {
    return this.querySelectorAll("[data-quiz-screen]");
  }

  /**
   * Returns all quiz answer inputs.
   * @returns {NodeList} A list of quiz answer elements.
   */
  get quizAnswers() {
    return this.querySelectorAll("[data-quiz-answer]");
  }

  /**
   * Returns an array of selected answer values from the currently visible quiz screen.
   * @returns {string[]} An array of selected answer values.
   */
  get selectedAnswers() {
    return [...this.quizAnswers]
      .filter((input) => input.checked)
      .map((input) => input.value);
  }

  /**
   * Returns an ordered array of correct answers from each quiz screen's data-value attribute.
   * @returns {string[]} An array of correct answer values in screen order.
   */
  get allAnswers() {
    return [...this.quizScreens]
      .map((screen) => screen.dataset.value)
      .filter((value) => value !== undefined);
  }

  /**
   * Returns all quiz control elements.
   * @returns {NodeList} A list of quiz controls elements.
   */
  get quizControls() {
    return this.querySelectorAll("[data-quiz-control]");
  }

  /**
   * Returns the quiz score element.
   * @returns {Element} The quiz result element.
   */
  get resultsElement() {
    return this.querySelector("[data-progress-result]");
  }

  /** 
   * Returns the quiz progress element.
   * @returns {Element} The quiz progress element.
   */
  get progressElement() {
    return this.querySelector("#progress-wrapper-results");
  }

  /**
   * Returns the quiz form element.
   * @returns {Element} The quiz form element.
   */
  get form() {
    return this.querySelector("[data-quiz-form]");
  }

  /**
* Returns the exclusive form message element.
* @returns {HTMLElement}
*/
  get message() {
    return this.querySelector("[data-quiz-form-message]");
  }
}

customElements.define("quiz-app", Quiz);
